import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import businessScopingData from '../../../shared/data/businessScoping.json'
import technologyScopingData from '../../../shared/data/technologyScoping.json'
import riskAppetiteData from '../../../shared/data/riskAppetite.json'
import maturityAssessmentData from '../../../shared/data/maturityAssessment.json'
import responseOptions from '../../../shared/data/responseOptions.json'
import autoDisableRules from '../../../shared/data/maturityAutoDisable.json'
import { calculateScores } from '../../../shared/utils/scoring'
import { hasResponseValue } from '../../../shared/utils/responseValue'
import { logScoringEvent, logValidationFlag } from '../../../shared/utils/auditLogger'
import { computeAssessmentDiff } from '../../../shared/utils/assessmentDiff'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import ChangeReasonModal from '../components/ChangeReasonModal'
import { getBusinessScopingIndustryValue, getDefaultFrameworks, getDefaultDataTypes, getIndustry, getSubCategory } from '../../../shared/data/industries'
import RiskEducationPanel from '../components/RiskEducationPanel'
import TechnologyScopingStep from '../components/TechnologyScopingStep'
import EngagementDriverSection from '../components/EngagementDriverSection'
import MultiSelectDropdown from '../../../shared/components/ui/MultiSelectDropdown'
import RiskAppetiteSelect from '../components/RiskAppetiteSelect'

// Context Info Tooltip component
function ContextTooltip({ contextQuestions }) {
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef(null)

  if (!contextQuestions) return null

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
        aria-label="Context factors information"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="absolute z-50 left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg"
        >
          <p className="font-semibold mb-1">Context Factors:</p>
          <p className="text-gray-300">{contextQuestions}</p>
          <div className="absolute left-4 bottom-0 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
        </div>
      )}
    </div>
  )
}

// Assessment Flow Order:
// 1. Business Scoping - organizational context (industry, size, data types)
// 2. Technology Scoping - technology platforms (cloud, IdP, EDR, backup)
// 3. Risk Appetite - risk tolerance (1-5 scale across 10 domains)
// 4. Maturity Assessment - 54-question security control evaluation
// 5. Threat Context - threat profile selection (TIDA step)
// 6. Impact Calibration - financial inputs for loss modeling (TIDA step)
const STEPS = [
  {
    id: 'business-scoping',
    name: 'Business Scoping',
    sections: businessScopingData.sections
  },
  {
    id: 'technology-scoping',
    name: 'Technology',
    sections: [{
      id: 'technology-scoping',
      name: 'Technology Environment',
      description: 'Identify your primary technology platforms to tailor security recommendations and threat analysis.',
      questions: [] // Handled by TechnologyScopingStep component
    }]
  },
  {
    id: 'risk-appetite',
    name: 'Risk Appetite',
    sections: [{
      id: 'risk-appetite',
      name: 'Risk Appetite Assessment',
      description: 'Evaluate your organization\'s tolerance for different types of risk.',
      questions: riskAppetiteData.questions
    }]
  },
  {
    id: 'maturity-assessment',
    name: 'Maturity Assessment',
    sections: maturityAssessmentData.sections
  },
]

function Assessment() {
  const { assessmentId } = useParams()
  const navigate = useNavigate()
  const { getAssessment, saveResponse, updateAssessment, completeAssessment, recalculateAndSaveScores, updateClient, refreshClients, loading, clients } = useAssessment()
  const { user, profile } = useAuth()
  const [searchParams] = useSearchParams()
  const isEditMode = searchParams.get('mode') === 'edit'

  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [localResponses, setLocalResponses] = useState({})
  const originalResponsesRef = useRef(null)
  const [showChangeReasonModal, setShowChangeReasonModal] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [hasEdits, setHasEdits] = useState(false)
  const [expandedNotes, setExpandedNotes] = useState({}) // Track which questions have notes expanded
  const [questionNotes, setQuestionNotes] = useState({}) // Store notes per question
  const [expandedEvidence, setExpandedEvidence] = useState({}) // Track which questions have evidence expanded
  const [questionEvidence, setQuestionEvidence] = useState({}) // Store selected evidence per question
  const [questionFlags, setQuestionFlags] = useState({}) // Follow-up flags per question
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false) // Filter to show only flagged questions
  const [autoDisableOverrides, setAutoDisableOverrides] = useState({}) // Assessor overrides for auto-disabled questions
  const hasInitializedNav = useRef(false) // Track if navigation position has been initialized
  const lastSyncedAssessmentId = useRef(null) // Track which assessment we've synced responses for
  const localEditTimestamp = useRef(0) // Prevent sync effect from overwriting in-flight local edits
  const [intakeProvenance, setIntakeProvenance] = useState(null) // Field provenance from client intake
  const [customFrameworkInput, setCustomFrameworkInput] = useState('') // Custom framework name input for rf-1
  const noteSaveTimerRef = useRef(null) // Debounce timer for note saves
  const flagSaveTimerRef = useRef(null) // Debounce timer for flag reason saves
  const isEditingNoteRef = useRef(false) // Prevent sync effect from overwriting notes during editing
  const isEditingFlagRef = useRef(false) // Prevent sync effect from overwriting flags during editing

  const { assessment, client } = getAssessment(assessmentId)

  // Fetch intake provenance for client-reported field indicators
  useEffect(() => {
    if (!client?.id) return
    supabase
      .from('client_intake_submissions')
      .select('field_provenance, status')
      .eq('client_id', client.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.status === 'submitted' && data?.field_provenance) {
          setIntakeProvenance(data.field_provenance)
        }
      })
  }, [client?.id])

  // Initialize navigation position only on first load
  useEffect(() => {
    if (assessment && !hasInitializedNav.current) {
      hasInitializedNav.current = true
      // Find current position from saved progress
      const stepIndex = STEPS.findIndex(s => s.id === assessment.currentStep)
      if (stepIndex !== -1) setCurrentStepIndex(stepIndex)
      setCurrentSectionIndex(assessment.currentSection || 0)
    }
  }, [assessment])

  // Snapshot original responses on first load in edit mode (for diff computation)
  useEffect(() => {
    if (isEditMode && assessment?.responses && !originalResponsesRef.current) {
      originalResponsesRef.current = JSON.parse(JSON.stringify(assessment.responses))
    }
  }, [isEditMode, assessment?.responses])

  // Sync from assessment.responses whenever context data changes
  useEffect(() => {
    if (!assessment?.id || !assessment?.responses) {
      return
    }

    // In edit mode, don't overwrite once user has made changes
    if (isEditMode && hasEdits) {
      return
    }

    // Skip sync if a local edit happened recently — let the save round-trip complete
    if (Date.now() - localEditTimestamp.current < 2000) {
      return
    }

    // Sync local state from assessment context
    // This handles: initial load, page refreshes, deployments, and saves
    setLocalResponses(assessment.responses)
    // Don't overwrite notes while user is actively typing (debounced save in progress)
    if (!isEditingNoteRef.current) {
      setQuestionNotes(assessment.responses?.notes || {})
    }
    setQuestionEvidence(assessment.responses?.evidence || {})
    if (!isEditingFlagRef.current) {
      setQuestionFlags(assessment.responses?.flags || assessment.responses?.notes?.flags || {})
    }
    setAutoDisableOverrides(assessment.responses?.notes?.autoDisableOverrides || {})
    lastSyncedAssessmentId.current = assessment.id
  }, [assessment])

  // Calculate overall risk appetite score from responses
  const overallRiskAppetite = useMemo(() => {
    const raResponses = localResponses.riskAppetite || {}
    const values = Object.values(raResponses).filter(v => typeof v === 'number')
    if (values.length === 0) return null
    const avg = values.reduce((a, b) => a + b, 0) / values.length
    return Math.round(avg * 10) / 10
  }, [localResponses.riskAppetite])

  // Compute auto-disabled maturity questions based on business scoping responses
  const autoDisabledQuestions = useMemo(() => {
    const bs = localResponses.businessScoping || {}
    const disabled = {} // { questionId: { reason, ruleId } }

    for (const rule of autoDisableRules.rules) {
      const { questionId, matchValues } = rule.condition
      const rawResponse = bs[questionId]
      // Normalize response — could be a string, object with .value, or array
      let responseValue
      if (Array.isArray(rawResponse)) {
        responseValue = rawResponse
      } else if (typeof rawResponse === 'object' && rawResponse !== null) {
        responseValue = rawResponse.value
      } else {
        responseValue = rawResponse
      }

      if (!responseValue) continue

      // Check if the response matches any of the disable conditions
      let matches = false
      if (Array.isArray(responseValue)) {
        // For multi-select (like rf-1): match if the array contains ONLY the specified values
        // or if the specified value appears in the array
        matches = matchValues.some(mv => {
          if (mv === 'none' || mv === 'none-of-the-above') {
            return responseValue.length === 0 || responseValue.includes(mv) || responseValue.includes('none')
          }
          return responseValue.includes(mv)
        })
      } else {
        matches = matchValues.some(mv => {
          if (typeof responseValue === 'string') {
            return responseValue === mv || responseValue.toLowerCase().includes(mv.toLowerCase())
          }
          return responseValue === mv
        })
      }

      if (matches) {
        for (const qId of rule.disables) {
          // Don't override if assessor has manually re-enabled
          if (!autoDisableOverrides[qId]) {
            disabled[qId] = { reason: rule.reason, ruleId: rule.id }
          }
        }
      }
    }

    return disabled
  }, [localResponses.businessScoping, autoDisableOverrides])

  // Map risk appetite score to a label for Business Scoping
  const getRiskAppetiteLabel = (score) => {
    if (score === null) return 'Not assessed'
    if (score >= 4.5) return 'Very High Tolerance'
    if (score >= 3.5) return 'High Tolerance'
    if (score >= 2.5) return 'Moderate Tolerance'
    if (score >= 1.5) return 'Low Tolerance'
    return 'Very Low Tolerance'
  }


  // Auto-fill client-linked fields (bs-0, bs-1, bs-3, bs-5) from client data
  // Only saves to context via saveResponse — the sync effect handles updating localResponses
  useEffect(() => {
    if (!client || !assessment?.responses) return

    // Find questions in business scoping sections (bs-0 is in section 1, others may vary)
    const findQuestion = (questionId) => {
      for (const section of businessScopingData.sections) {
        const question = section.questions.find(q => q.id === questionId)
        if (question) return question
      }
      return null
    }

    const clientFieldMappings = {
      'bs-0': { field: 'industry', options: findQuestion('bs-0')?.options },
      'bs-1': { field: 'annualRevenue', options: findQuestion('bs-1')?.options },
      'bs-3': { field: 'staffingSize', options: findQuestion('bs-3')?.options },
      'bs-5': { field: 'geographicalPresence', options: findQuestion('bs-5')?.options }
    }

    const existingBusinessScoping = assessment.responses.businessScoping || {}

    // Auto-populate bs-0a (sub-industry) from client.industrySubCategory
    if (client.industrySubCategory) {
      const subCat = getSubCategory(client.industry, client.industrySubCategory)
      if (subCat) {
        const currentBs0a = existingBusinessScoping['bs-0a']
        if (!currentBs0a || currentBs0a.value !== subCat.value) {
          saveResponse(assessmentId, 'businessScoping', 'bs-0a', {
            value: subCat.value,
            label: subCat.label,
            weight: 1.0
          })
        }
      }
    } else {
      // Clear bs-0a if no sub-category is set
      const currentBs0a = existingBusinessScoping['bs-0a']
      if (currentBs0a) {
        saveResponse(assessmentId, 'businessScoping', 'bs-0a', null)
      }
    }

    Object.entries(clientFieldMappings).forEach(([questionId, { field, options }]) => {
      if (!options) return

      let matchedOption = null
      let matchValue = null

      if (questionId === 'bs-0') {
        // For bs-0 (industry), use the composite key from industry + subCategory
        const compositeValue = getBusinessScopingIndustryValue(client.industry, client.industrySubCategory)
        if (compositeValue) {
          // Match by value field (composite key) or label as fallback
          matchedOption = options.find(opt => opt.value === compositeValue || opt.label === compositeValue)
          matchValue = matchedOption?.value || matchedOption?.label
        }
      } else if (questionId === 'bs-5') {
        // For bs-5 (geographical presence), match by value field
        const clientValue = client[field]
        if (clientValue) {
          matchedOption = options.find(opt => opt.value === clientValue)
          matchValue = matchedOption?.label // Use label as the stored value for display
        }
      } else {
        // For other fields, match by label
        const clientValue = client[field]
        if (clientValue) {
          matchedOption = options.find(opt => opt.label === clientValue)
          matchValue = matchedOption?.label
        }
      }

      if (matchedOption && matchValue) {
        const currentResponse = existingBusinessScoping[questionId]
        // Only save if the value is actually different or missing
        if (!currentResponse || currentResponse.value !== matchValue) {
          saveResponse(assessmentId, 'businessScoping', questionId, {
            value: matchValue,
            weight: matchedOption.weight
          })
        }
      }
    })

    // Auto-fill rf-1 (regulatory frameworks) from client.regulatoryFrameworks
    // Updates when industry changes — compares against industry defaults to detect admin changes
    const clientFrameworks = client.regulatoryFrameworks || []
    if (clientFrameworks.length > 0) {
      const currentRf1 = existingBusinessScoping['rf-1']
      const currentValues = Array.isArray(currentRf1?.value) ? currentRf1.value : []
      const sortedCurrent = [...currentValues].sort().join(',')
      const sortedNew = [...clientFrameworks].sort().join(',')
      if (sortedCurrent !== sortedNew) {
        saveResponse(assessmentId, 'businessScoping', 'rf-1', {
          value: clientFrameworks,
          weight: 1.0
        })
      }
    }

    // Auto-fill dt-1 (sensitive data types) from industry mapping
    // Recomputes when industry/sub-category changes; user can add more in the assessment
    const defaultDataTypes = getDefaultDataTypes(client.industry, client.industrySubCategory)
    if (defaultDataTypes.length > 0) {
      const currentDt1 = existingBusinessScoping['dt-1']
      const currentDtValues = Array.isArray(currentDt1?.value) ? currentDt1.value : []
      // Merge: keep any manual selections and add new industry defaults
      const merged = [...new Set([...currentDtValues, ...defaultDataTypes])]
      const sortedCurrent = [...currentDtValues].sort().join(',')
      const sortedMerged = [...merged].sort().join(',')
      if (sortedCurrent !== sortedMerged) {
        saveResponse(assessmentId, 'businessScoping', 'dt-1', {
          value: merged,
          weight: 1.0
        })
      }
    }
  // NOTE: client?.regulatoryFrameworks is intentionally excluded from deps.
  // Including it creates a feedback loop: user changes rf-1 → syncs to client → triggers
  // this effect → overwrites rf-1 with the (stale) client value. The rf-1 auto-fill
  // only needs to run on initial load (client.id change).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, client?.industry, client?.industrySubCategory, client?.annualRevenue, client?.staffingSize, client?.geographicalPresence, assessmentId])

  // Auto-populate bs-12 (Business Scoping "What is the organization's risk appetite?")
  // from the completed Risk Appetite section. bs-12 is rendered read-only
  // (linksToRiskAppetite), so the only path to a stored value is this effect.
  useEffect(() => {
    if (!assessmentId || overallRiskAppetite == null) return
    // Map the 1-5 score to one of bs-12's three options
    const mapped = overallRiskAppetite >= 3.5
      ? { value: 'High: Accepts Greater Risk/Reward', weight: 0.75 }
      : overallRiskAppetite >= 2.5
      ? { value: 'Moderate: Calculated Risk', weight: 1 }
      : { value: 'Low: Highly Cautious', weight: 1.5 }

    const current = localResponses.businessScoping?.['bs-12']
    if (current?.value === mapped.value && current?.weight === mapped.weight) return

    saveResponse(assessmentId, 'businessScoping', 'bs-12', mapped)
  }, [overallRiskAppetite, assessmentId, localResponses.businessScoping?.['bs-12']?.value, localResponses.businessScoping?.['bs-12']?.weight])

  // Show loading state while context is fetching data
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading assessment...</p>
        </div>
      </div>
    )
  }

  if (!assessment || !client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Assessment not found</h2>
        <button onClick={() => navigate('/clients')} className="btn-primary mt-4">
          Back to Clients
        </button>
      </div>
    )
  }

  const currentStep = STEPS[currentStepIndex]
  const currentSection = currentStep.sections[currentSectionIndex]

  const getResponseKey = (stepId) => {
    if (stepId === 'business-scoping') return 'businessScoping'
    if (stepId === 'technology-scoping') return 'technologyScoping'
    if (stepId === 'risk-appetite') return 'riskAppetite'
    return 'maturityAssessment'
  }

  const handleResponseChange = (questionId, value, weight = null) => {
    if (isEditMode) setHasEdits(true)
    const responseKey = getResponseKey(currentStep.id)

    // Store both value and weight for Business Scoping questions
    const responseData = weight !== null ? { value, weight } : value

    const newResponses = {
      ...localResponses,
      [responseKey]: {
        ...(localResponses[responseKey] || {}),
        [questionId]: responseData
      }
    }

    // Clear dependent conditional questions when parent changes
    // e.g., clear bs-5a (international regions) when bs-5 changes to domestic
    if (questionId === 'bs-5' && responseKey === 'businessScoping') {
      const selectedLabel = typeof value === 'string' ? value : value
      const isDomestic = selectedLabel === 'Domestic Only (US)' || selectedLabel === 'domestic'
      if (isDomestic) {
        newResponses[responseKey]['bs-5a'] = { value: [], weight: 1.0 }
        saveResponse(assessmentId, responseKey, 'bs-5a', { value: [], weight: 1.0 })
      }
    }

    // Sync rf-1 (regulatory frameworks) back to client record for policy recommendations
    if (questionId === 'rf-1' && responseKey === 'businessScoping' && assessment?.clientId) {
      const selectedValues = Array.isArray(value) ? value : (value?.value || [])
      const frameworks = selectedValues.filter(v => v !== 'none')
      updateClient(assessment.clientId, { regulatoryFrameworks: frameworks })
    }

    localEditTimestamp.current = Date.now()
    setLocalResponses(newResponses)
    saveResponse(assessmentId, responseKey, questionId, responseData)
  }

  const handleNoteChange = (questionId, note) => {
    const newNotes = {
      ...questionNotes,
      [questionId]: note
    }
    setQuestionNotes(newNotes)
    isEditingNoteRef.current = true

    // Debounce save to prevent context churn on every keystroke
    if (noteSaveTimerRef.current) {
      clearTimeout(noteSaveTimerRef.current)
    }
    noteSaveTimerRef.current = setTimeout(() => {
      saveResponse(assessmentId, 'notes', questionId, note)
      // Allow sync effect to update notes again after save settles
      setTimeout(() => { isEditingNoteRef.current = false }, 500)
    }, 600)
  }

  const toggleFlag = (questionId) => {
    const isFlagged = !!questionFlags[questionId]
    const newFlags = { ...questionFlags }
    if (isFlagged) {
      delete newFlags[questionId]
    } else {
      newFlags[questionId] = { flaggedAt: new Date().toISOString(), reason: '' }
    }
    setQuestionFlags(newFlags)
    saveResponse(assessmentId, 'flags', questionId, isFlagged ? null : newFlags[questionId])
  }

  const updateFlagReason = (questionId, reason) => {
    const newFlags = { ...questionFlags, [questionId]: { ...questionFlags[questionId], reason } }
    setQuestionFlags(newFlags)
    isEditingFlagRef.current = true
    // Debounce save on its own timer
    if (flagSaveTimerRef.current) clearTimeout(flagSaveTimerRef.current)
    flagSaveTimerRef.current = setTimeout(() => {
      saveResponse(assessmentId, 'flags', questionId, newFlags[questionId])
      setTimeout(() => { isEditingFlagRef.current = false }, 500)
    }, 600)
  }

  const toggleNotes = (questionId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }))
  }

  const handleEvidenceChange = (questionId, evidenceItem, isChecked) => {
    const currentEvidence = questionEvidence[questionId] || []
    let newEvidence
    if (isChecked) {
      newEvidence = [...currentEvidence, evidenceItem]
    } else {
      newEvidence = currentEvidence.filter(e => e !== evidenceItem)
    }
    const updatedEvidence = {
      ...questionEvidence,
      [questionId]: newEvidence
    }
    setQuestionEvidence(updatedEvidence)
    // Save evidence to assessment responses under an 'evidence' key
    saveResponse(assessmentId, 'evidence', questionId, newEvidence)
  }

  const toggleEvidence = (questionId) => {
    setExpandedEvidence(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }))
  }

  const getCurrentResponse = (questionId) => {
    const responseKey = getResponseKey(currentStep.id)
    const response = localResponses[responseKey]?.[questionId]
    // Handle both simple values and {value, weight} objects
    if (response && typeof response === 'object' && 'value' in response) {
      return response.value
    }
    return response || ''
  }

  const getSectionProgress = () => {
    // Technology scoping step uses separate component
    if (currentStep.id === 'technology-scoping') {
      const techQuestions = technologyScopingData.sections.reduce(
        (sum, section) => sum + section.questions.length, 0
      )
      const answeredCount = Object.keys(localResponses.technologyScoping || {}).length
      return { answered: answeredCount, total: techQuestions }
    }
    // Check if a conditional question is currently visible based on its showWhen rule
    const isQuestionVisible = (q) => {
      if (!q.showWhen) return true
      const depResponse = localResponses.businessScoping?.[q.showWhen.questionId]
      const depValue = typeof depResponse === 'object' ? depResponse?.value : depResponse
      return depValue && q.showWhen.values.includes(depValue)
    }

    const visibleQuestions = currentSection.questions.filter(isQuestionVisible)
    const answered = visibleQuestions.filter(q => {
      const response = getCurrentResponse(q.id)
      return response !== '' && response !== null && response !== undefined
    }).length
    return { answered, total: visibleQuestions.length }
  }

  // Check if a conditional question is currently visible based on its showWhen rule
  const isQuestionVisible = (q) => {
    if (!q.showWhen) return true
    const depResponse = localResponses.businessScoping?.[q.showWhen.questionId]
    const depValue = typeof depResponse === 'object' ? depResponse?.value : depResponse
    return depValue && q.showWhen.values.includes(depValue)
  }

  const getTotalProgress = () => {
    let total = 0
    let answered = 0
    STEPS.forEach(step => {
      // Tech scoping uses a separate component for its UI but its responses
      // live in the same localResponses.technologyScoping bag. Only count
      // top-level schema questions — extras like free-text "Other" sub-keys
      // (ts-7-other, etc.) are saved under the same column and would otherwise
      // inflate the answered count.
      if (step.id === 'technology-scoping') {
        const schemaIds = new Set(
          technologyScopingData.sections.flatMap(s => s.questions.map(q => q.id))
        )
        total += schemaIds.size
        const responses = localResponses.technologyScoping || {}
        for (const id of schemaIds) {
          if (hasResponseValue(responses[id])) answered++
        }
        return
      }
      const responseKey = getResponseKey(step.id)
      step.sections.forEach(section => {
        section.questions.forEach(q => {
          // Skip conditional questions that aren't currently visible
          if (!isQuestionVisible(q)) return
          total++
          if (hasResponseValue(localResponses[responseKey]?.[q.id])) {
            answered++
          }
        })
      })
    })
    // Clamp so we never display negative remaining or >100% complete
    const safeAnswered = Math.min(answered, total)
    const percentage = total > 0 ? Math.min(100, Math.round((safeAnswered / total) * 100)) : 0
    return { answered: safeAnswered, total, percentage }
  }

  // Check if a specific step is complete (all questions answered)
  const isStepComplete = (step) => {
    // Technology scoping step
    if (step.id === 'technology-scoping') {
      const techQuestions = technologyScopingData.sections.reduce(
        (sum, section) => sum + section.questions.length, 0
      )
      const answeredCount = Object.keys(localResponses.technologyScoping || {}).length
      return answeredCount >= techQuestions
    }

    // Standard steps - check all sections and questions
    const responseKey = getResponseKey(step.id)
    let totalQuestions = 0
    let answeredQuestions = 0

    step.sections.forEach(section => {
      section.questions.forEach(q => {
        // Skip conditional questions that aren't currently visible
        if (!isQuestionVisible(q)) return
        totalQuestions++
        if (hasResponseValue(localResponses[responseKey]?.[q.id])) {
          answeredQuestions++
        }
      })
    })

    return totalQuestions > 0 && answeredQuestions >= totalQuestions
  }

  const handleNext = () => {
    if (currentSectionIndex < currentStep.sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1)
      updateAssessment(assessmentId, { currentSection: currentSectionIndex + 1 })
    } else if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1)
      setCurrentSectionIndex(0)
      updateAssessment(assessmentId, {
        currentStep: STEPS[currentStepIndex + 1].id,
        currentSection: 0
      })
    }
    window.scrollTo(0, 0)
  }

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1)
      updateAssessment(assessmentId, { currentSection: currentSectionIndex - 1 })
    } else if (currentStepIndex > 0) {
      const prevStep = STEPS[currentStepIndex - 1]
      setCurrentStepIndex(currentStepIndex - 1)
      setCurrentSectionIndex(prevStep.sections.length - 1)
      updateAssessment(assessmentId, {
        currentStep: prevStep.id,
        currentSection: prevStep.sections.length - 1
      })
    }
    window.scrollTo(0, 0)
  }

  const handleComplete = async () => {
    const scores = calculateScores(localResponses)

    // Persist audit trail (non-blocking, fails silently)
    if (scores._auditEventData) {
      try {
        const auditEntry = await logScoringEvent(
          supabase, assessmentId, 'score_calculated', scores._auditEventData, user?.id
        )
        if (auditEntry?.id && scores._validation?.length > 0) {
          for (const flag of scores._validation) {
            await logValidationFlag(
              supabase, assessmentId, auditEntry.id,
              flag.checkName, flag.severity, flag.message, flag.details
            )
          }
        }
      } catch (e) {
        // Audit logging should never block assessment completion
        console.error('Audit log error:', e)
      }
    }

    completeAssessment(assessmentId, scores)
    // Redirect to Program Overview (the legacy Results page has been removed)
    if (client?.id) {
      navigate(`/clients/${client.id}?section=program-overview`)
    } else {
      navigate('/clients')
    }
  }

  // Edit mode: save changes with diff + reason
  const handleEditSave = async (reason) => {
    if (!originalResponsesRef.current) return
    setEditSaving(true)
    try {
      const changes = computeAssessmentDiff(originalResponsesRef.current, localResponses)
      console.log('[Assessment] Edit save - changes detected:', changes.length, changes.map(c => `${c.section}/${c.questionId}`))
      if (changes.length === 0) {
        setShowChangeReasonModal(false)
        setEditSaving(false)
        navigate(`/clients/${client.id}?section=maturity-assessment`)
        return
      }

      // Save changed response sections to database
      const changedSections = [...new Set(changes.map(c => c.section))]
      const sectionDbMap = {
        riskAppetite: 'risk_appetite',
        businessScoping: 'business_scoping',
        technologyScoping: 'technology_scoping',
        maturityAssessment: 'maturity_assessment'
      }

      for (const section of changedSections) {
        const dbColumn = sectionDbMap[section]
        if (!dbColumn) continue
        const { error: saveErr } = await supabase
          .from('assessment_responses')
          .update({ [dbColumn]: localResponses[section] })
          .eq('assessment_id', assessmentId)
        if (saveErr) throw saveErr
      }

      // Recalculate scores
      const scores = calculateScores(localResponses)
      await recalculateAndSaveScores(assessmentId, scores)

      const previousScore = assessment.scores?.overall?.score
      const newScore = scores.overall?.score

      // Insert change log rows
      const { data: { session } } = await supabase.auth.getSession()
      const changeRows = changes.map(c => ({
        assessment_id: assessmentId,
        user_id: user.id,
        user_email: user.email,
        section: c.sectionDbKey,
        question_id: c.questionId,
        question_label: c.questionLabel || c.questionId,
        previous_value: c.previousValue !== undefined ? c.previousValue : null,
        new_value: c.newValue !== undefined ? c.newValue : null,
        reason,
        previous_overall_score: previousScore ?? null,
        new_overall_score: newScore ?? null
      }))

      const { error: changeLogErr } = await supabase.from('assessment_change_log').insert(changeRows)
      if (changeLogErr) console.error('[Assessment] Change log insert error:', changeLogErr)

      // Also log to general audit trail
      logAuditEvent({
        category: 'change',
        action: 'assessment.edited',
        description: `Edited completed assessment: ${changes.length} question${changes.length !== 1 ? 's' : ''} changed. Score: ${previousScore ?? 'N/A'}% → ${newScore ?? 'N/A'}%. Reason: ${reason}`,
        clientId: client?.id,
        clientName: client?.name,
        resourceType: 'assessment',
        resourceId: assessmentId
      })

      setShowChangeReasonModal(false)
      // Refresh context so all pages see the updated responses
      refreshClients()
      navigate(`/clients/${client.id}?section=maturity-assessment`)
    } catch (err) {
      console.error('[Assessment] Edit save error:', err)
      alert(`Save failed: ${err.message || 'Unknown error'}. Check the browser console for details.`)
      setShowChangeReasonModal(false)
    } finally {
      setEditSaving(false)
    }
  }

  const isLastSection = currentStepIndex === STEPS.length - 1 &&
    currentSectionIndex === currentStep.sections.length - 1

  const progress = getTotalProgress()
  const sectionProgress = getSectionProgress()

  // Render question input based on type
  const renderQuestionInput = (question) => {
    // Risk Appetite questions - use dropdown select
    if (currentStep.id === 'risk-appetite') {
      // Get response and convert to string for HTML select comparison
      // (HTML option values are always strings, so we need consistent types)
      const currentValue = getCurrentResponse(question.id)
      const selectValue = currentValue !== null && currentValue !== undefined && currentValue !== ''
        ? String(currentValue)
        : ''

      return (
        <RiskAppetiteSelect
          value={selectValue}
          options={question.options}
          onChange={(selectedValue) => handleResponseChange(question.id, selectedValue)}
        />
      )
    }

    // Business Scoping questions
    if (currentStep.id === 'business-scoping') {
      // Check if this is an auto-populated field from client data
      if (question.autoPopulatedFromClient && question.clientField) {
        // For bs-0a (sub-industry), hide when the industry has no sub-categories
        if (question.id === 'bs-0a') {
          const industry = getIndustry(client?.industry)
          if (!industry?.subCategories) return null
          const subCat = getSubCategory(client?.industry, client?.industrySubCategory)
          if (!subCat) return null
        }

        let displayValue
        if (question.id === 'bs-0a') {
          const subCat = getSubCategory(client?.industry, client?.industrySubCategory)
          displayValue = subCat?.label || 'Not configured'
        } else {
          const clientValue = client?.[question.clientField]
          const matchedOption = question.options?.find(opt => opt.label === clientValue || opt.value === clientValue)
          displayValue = matchedOption?.label || clientValue || 'Not configured'
        }

        // Determine if field was client-reported via intake
        const provenanceKeyMap = { industry: 'industry', annualRevenue: 'annual_revenue', staffingSize: 'staffing_size', geographicalPresence: 'geographical_presence', industrySubCategory: 'industry_sub_category' }
        const provKey = provenanceKeyMap[question.clientField]
        const isClientReported = provKey && intakeProvenance?.[provKey]?.source === 'client_intake'
        const wasModifiedByAdmin = isClientReported && intakeProvenance[provKey].modified_by_admin

        return (
          <div className="space-y-2">
            {isClientReported ? (
              <div className={`rounded-lg p-4 ${wasModifiedByAdmin
                ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
                : 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
              }`}>
                <div className="flex items-start space-x-3">
                  <svg className={`w-5 h-5 mt-0.5 ${wasModifiedByAdmin ? 'text-amber-500' : 'text-teal-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div>
                    <p className={`text-sm font-medium ${wasModifiedByAdmin
                      ? 'text-amber-800 dark:text-amber-300'
                      : 'text-teal-800 dark:text-teal-300'
                    }`}>
                      {wasModifiedByAdmin ? 'Client Reported (Admin Modified)' : 'Client Reported'}
                    </p>
                    <p className={`text-xs mt-1 ${wasModifiedByAdmin
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-teal-700 dark:text-teal-400'
                    }`}>
                      This value was submitted by the client via their intake form. To change it, update in the Edit Client modal on the Client Overview page.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-teal-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-teal-800 dark:text-teal-300">
                      Auto-populated from Client Overview
                    </p>
                    <p className="text-xs text-teal-700 dark:text-teal-400 mt-1">
                      This field is set from the client's profile. To change this value, update in the Edit Client modal on the Client Overview page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Read-only display of the value */}
            <div className={`w-full p-3 rounded-lg ${isClientReported && !wasModifiedByAdmin
              ? 'border-l-4 border-l-teal-400 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
              : 'border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
            } text-gray-700 dark:text-gray-300`}>
              {displayValue}
            </div>
          </div>
        )
      }

      // Check if this is the risk appetite link question (read-only)
      if (question.linksToRiskAppetite) {
        const currentValue = getCurrentResponse(question.id)

        return (
          <div className="space-y-2">
            {/* Info box showing the Risk Appetite score */}
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Auto-populated from Risk Appetite Assessment
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    Your organization's risk appetite score: <strong>{overallRiskAppetite ?? 'Not yet assessed'}</strong>
                    {overallRiskAppetite !== null && (
                      <span className="ml-2">({getRiskAppetiteLabel(overallRiskAppetite)})</span>
                    )}
                  </p>
                  {overallRiskAppetite === null && (
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-2">
                      Complete the Risk Appetite section first to automatically populate this value.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Read-only display of the value */}
            <div className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              {currentValue || 'Pending Risk Appetite assessment...'}
            </div>
          </div>
        )
      }

      // Check if this non-autoPopulated question has intake data (dual-write mapped fields)
      const intakeQuestionMap = {
        'sl-1': 'operational_hq',
        'sl-2': 'office_location_states',
        'sl-3': 'data_residency_states',
        'bs-28': 'security_function_model',
        'bs-40': 'business_model'
      }
      const intakeFieldKey = intakeQuestionMap[question.id]
      const intakeReported = intakeFieldKey && intakeProvenance?.[intakeFieldKey]?.source === 'client_intake'
      const intakeAdminModified = intakeReported && intakeProvenance[intakeFieldKey].modified_by_admin

      // Fields that live in business_scoping (not the clients table) — editable here, not in Edit Client modal
      const businessScopingOnlyFields = ['sl-1', 'bs-40']
      const isBsOnly = businessScopingOnlyFields.includes(question.id)
      const bannerHint = isBsOnly
        ? 'This value was submitted by the client via their intake form. It can be changed directly here.'
        : 'This value was submitted by the client via their intake form. To change it, update in the Edit Client modal on the Client Overview page.'

      const clientReportedBanner = intakeReported ? (
        <div className={`rounded-lg p-3 mb-3 ${intakeAdminModified
          ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
          : 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
        }`}>
          <div className="flex items-start space-x-2">
            <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${intakeAdminModified ? 'text-amber-500' : 'text-teal-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div>
              <p className={`text-xs font-medium ${intakeAdminModified
                ? 'text-amber-800 dark:text-amber-300'
                : 'text-teal-800 dark:text-teal-300'
              }`}>
                {intakeAdminModified ? 'Client Reported (Admin Modified)' : 'Client Reported'}
              </p>
              <p className={`text-xs mt-0.5 ${intakeAdminModified
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-teal-700 dark:text-teal-400'
              }`}>
                {bannerHint}
              </p>
            </div>
          </div>
        </div>
      ) : null

      // Multi-select questions - use checkboxes or dropdown based on option count
      if (question.responseType === 'multi-select' && question.options && question.options.length > 0) {
        const currentResponse = localResponses.businessScoping?.[question.id]
        // Handle array of values for multi-select
        const selectedValues = Array.isArray(currentResponse?.value)
          ? currentResponse.value
          : currentResponse?.value
            ? [currentResponse.value]
            : []

        // Use MultiSelectDropdown for all multi-select questions
        const dropdownOptions = question.options.map(opt => ({
          value: opt.value || opt.label,
          label: opt.label,
          hasPrivacyLaw: opt.hasPrivacyLaw || false
        }))

        return (
          <div className="space-y-3">
            {clientReportedBanner}
            {question.helperText && (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-3">{question.helperText}</p>
            )}
            <MultiSelectDropdown
              options={dropdownOptions}
              selectedValues={selectedValues}
              onChange={(newValues) => {
                handleResponseChange(question.id, newValues.length > 0 ? newValues : [], 1.0)
              }}
              placeholder="Select all that apply..."
              searchPlaceholder="Search options..."
              maxDisplayTags={4}
            />

            {/* Custom / Other Frameworks — only for rf-1 */}
            {question.id === 'rf-1' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Custom / Other Frameworks</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Add any frameworks not listed above. These will be staged for review and added to your compliance profile.
                </p>

                {/* Show custom frameworks as removable pills */}
                {(() => {
                  const knownValues = (question.options || []).map(o => o.value || o.label)
                  const customValues = selectedValues.filter(v => !knownValues.includes(v) && v !== 'none' && v !== 'none-of-the-above')
                  if (customValues.length === 0) return null
                  return (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {customValues.map(fw => (
                        <span key={fw} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          {fw}
                          <button
                            type="button"
                            onClick={() => {
                              const newValues = selectedValues.filter(v => v !== fw)
                              handleResponseChange(question.id, newValues.length > 0 ? newValues : [], 1.0)
                            }}
                            className="ml-0.5 hover:text-amber-600 dark:hover:text-amber-100"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )
                })()}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customFrameworkInput}
                    onChange={(e) => setCustomFrameworkInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customFrameworkInput.trim()) {
                        e.preventDefault()
                        const name = customFrameworkInput.trim()
                        if (!selectedValues.includes(name)) {
                          const newValues = [...selectedValues.filter(v => v !== 'none' && v !== 'none-of-the-above'), name]
                          handleResponseChange(question.id, newValues, 1.0)
                          // Stage custom framework for admin review
                          if (client?.id) {
                            supabase.from('framework_staging').insert({
                              org_id: client.orgId || null,
                              client_id: client.id,
                              submitted_name: name,
                              submitted_by: 'assessor'
                            }).then(({ error }) => {
                              if (error) console.warn('[Assessment] Framework staging insert failed:', error.message)
                            })
                          }
                        }
                        setCustomFrameworkInput('')
                      }
                    }}
                    placeholder="e.g., NERC CIP, MARS-E, TX-RAMP..."
                    className="flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const name = customFrameworkInput.trim()
                      if (!name || selectedValues.includes(name)) return
                      const newValues = [...selectedValues.filter(v => v !== 'none' && v !== 'none-of-the-above'), name]
                      handleResponseChange(question.id, newValues, 1.0)
                      if (client?.id) {
                        supabase.from('framework_staging').insert({
                          org_id: client.orgId || null,
                          client_id: client.id,
                          submitted_name: name,
                          submitted_by: 'assessor'
                        }).then(({ error }) => {
                          if (error) console.warn('[Assessment] Framework staging insert failed:', error.message)
                        })
                      }
                      setCustomFrameworkInput('')
                    }}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!customFrameworkInput.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }

      // Questions with predefined options - use dropdown select
      if (question.options && question.options.length > 0) {
        const rawValue = getCurrentResponse(question.id) || ''
        // Determine the select value: use the raw value as-is if it matches an option's value field,
        // otherwise check if it matches a label (legacy responses stored as labels)
        const hasExplicitValues = question.options.some(opt => opt.value)
        let selectValue = rawValue
        if (hasExplicitValues) {
          // Options have explicit value fields — match against those
          const matchedByValue = question.options.find(opt => opt.value === rawValue)
          const matchedByLabel = !matchedByValue && question.options.find(opt => opt.label === rawValue)
          selectValue = matchedByValue ? matchedByValue.value : matchedByLabel ? matchedByLabel.value : rawValue
        }
        // displayValue is used by downstream conditional checks (e.g., bs-43 GRC follow-up)
        const displayValue = selectValue

        return (
          <div>
            {clientReportedBanner}
            <select
              value={selectValue}
              onChange={(e) => {
                const val = e.target.value
                const selectedOption = question.options.find(opt => (opt.value || opt.label) === val || opt.label === val)
                if (selectedOption) {
                  handleResponseChange(question.id, selectedOption.value || selectedOption.label, selectedOption.weight)
                } else {
                  handleResponseChange(question.id, '', null)
                }
              }}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a response...</option>
              {question.options.map((option, idx) => (
                <option key={idx} value={option.value || option.label}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* GRC Platform follow-up — show when bs-43 is "yes" or "evaluating" */}
            {question.id === 'bs-43' && question.grcPlatformOptions && (displayValue === 'yes' || displayValue === 'evaluating') && (() => {
              const grcRaw = localResponses.businessScoping?.['bs-43-platform']
              const selectedPlatform = typeof grcRaw === 'string' ? grcRaw : (grcRaw?.value || '')
              const customRaw = localResponses.businessScoping?.['bs-43-platform-other']
              const customPlatform = typeof customRaw === 'string' ? customRaw : (customRaw?.value || '')

              return (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {displayValue === 'yes' ? 'Which GRC platform does your organization use?' : 'Which platform are you evaluating?'}
                  </label>
                  <select
                    value={selectedPlatform}
                    onChange={(e) => {
                      const val = e.target.value
                      const responseKey = 'businessScoping'
                      const updated = {
                        ...localResponses,
                        [responseKey]: {
                          ...(localResponses[responseKey] || {}),
                          'bs-43-platform': val,
                          ...(val !== 'Other' ? { 'bs-43-platform-other': '' } : {})
                        }
                      }
                      localEditTimestamp.current = Date.now()
                      setLocalResponses(updated)
                      saveResponse(assessmentId, responseKey, 'bs-43-platform', val)
                      if (val !== 'Other') {
                        saveResponse(assessmentId, responseKey, 'bs-43-platform-other', '')
                      }
                    }}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a platform...</option>
                    {question.grcPlatformOptions.map(platform => (
                      <option key={platform} value={platform}>{platform}</option>
                    ))}
                  </select>

                  {selectedPlatform === 'Other' && (
                    <input
                      type="text"
                      value={localResponses.businessScoping?.['bs-43-platform-other']?.value || customPlatform}
                      onChange={(e) => handleResponseChange('bs-43-platform-other', e.target.value, null)}
                      placeholder="Enter GRC platform name..."
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  )}
                </div>
              )
            })()}
          </div>
        )
      }

      // Free text questions
      return (
        <div>
          {clientReportedBanner}
          <textarea
            value={getCurrentResponse(question.id)}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={3}
            placeholder="Enter your response..."
          />
        </div>
      )
    }

    // Maturity Assessment questions - scaled (4-point) or binary (Yes/No)
    if (currentStep.id === 'maturity-assessment') {
      const currentResponse = getCurrentResponse(question.id)
      // Look up question metadata to check if binary
      const questionMeta = maturityAssessmentData.sections
        .flatMap(s => s.questions)
        .find(q => q.id === question.id)
      const isBinary = questionMeta?.responseType === 'binary'

      const rubric = questionMeta?.rubric
      const scaledOptions = responseOptions.maturityResponses
      const binaryOptions = [
        { value: 'no', label: 'No', description: 'Not implemented or not in place' },
        { value: 'yes', label: 'Yes', description: 'Fully implemented and effective' }
      ]
      const options = isBinary ? binaryOptions : scaledOptions

      return (
        <div>
          {/* Main response buttons */}
          <div className={`grid gap-3 ${isBinary ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
            {options.map(option => (
              <button
                key={option.value}
                onClick={() => handleResponseChange(question.id, option.value)}
                className={`p-3 rounded-xl text-left transition-all duration-200 relative group ${
                  currentResponse === option.value
                    ? 'bg-white dark:bg-teal-900/20 border-2 border-primary-400 dark:border-teal-500 ring-2 ring-primary-200/50 dark:ring-teal-500/30 shadow-lg shadow-primary-100/50 dark:shadow-teal-900/20'
                    : 'bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-teal-600 hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold text-sm ${currentResponse === option.value ? 'text-primary-700 dark:text-teal-300' : 'text-gray-900 dark:text-white'}`}>{option.label}</span>
                  {currentResponse === option.value && (
                    <svg className="w-4 h-4 text-primary-500 dark:text-teal-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span className={`block text-xs leading-relaxed ${currentResponse === option.value ? 'text-primary-600 dark:text-teal-400' : 'text-gray-500 dark:text-gray-400'}`}>{option.description}</span>
                {rubric?.[option.value] && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-[#012438] dark:bg-white text-white dark:text-gray-800 text-xs rounded-lg shadow-xl ring-1 ring-[#8AD0D6]/40 dark:ring-gray-200 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30 leading-relaxed">
                    <span className="font-bold text-[#8AD0D6] dark:text-teal-700 uppercase text-[10px] tracking-wider">Criteria:</span> {rubric[option.value]}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#012438] dark:border-t-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* N/A + Unknown secondary buttons */}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => {
                handleResponseChange(question.id, currentResponse === 'na' ? '' : 'na')
              }}
              className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                currentResponse === 'na'
                  ? 'border-gray-400 dark:border-gray-500 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              N/A
            </button>
            <button
              onClick={() => {
                handleResponseChange(question.id, currentResponse === 'unknown' ? '' : 'unknown')
                // Auto-flag for follow-up when Unknown is selected
                if (currentResponse !== 'unknown' && !questionFlags[question.id]) {
                  toggleFlag(question.id)
                }
              }}
              className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                currentResponse === 'unknown'
                  ? 'border-orange-300 dark:border-orange-700 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Unknown
            </button>
            {currentResponse === 'na' && (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">Excluded from scoring</span>
            )}
            {currentResponse === 'unknown' && (
              <span className="text-xs text-orange-500 dark:text-orange-400 italic">Scored as 0 — flagged for follow-up</span>
            )}
          </div>

        </div>
      )
    }

    return null
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Sticky Header - Client info, title, progress bar, and step navigation */}
      <div className="sticky top-16 z-20 bg-gray-50 dark:bg-gray-900 pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 border-b border-gray-200 dark:border-gray-700">
        <div className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-4">
              <Link
                to={`/clients/${client.id}`}
                className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Client Home
              </Link>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Assessment for {client.name}</h1>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">{progress.percentage}% complete</span>
          </div>

          {/* Edit mode banner with actions */}
          {isEditMode && (
            <div className="flex items-center justify-between px-4 py-2.5 mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <span className="font-semibold">Edit Mode</span> &ndash; Changes will be tracked in the change log.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/clients/${client.id}?section=maturity-assessment`)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                {hasEdits && (
                  <button
                    onClick={() => setShowChangeReasonModal(true)}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Overall Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
            <div
              className="bg-primary-600 dark:bg-accent-300 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>

          {/* Step Navigation */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const stepComplete = isStepComplete(step)
              const isCurrent = idx === currentStepIndex
              const isPast = idx < currentStepIndex
              // Check if this step has any flagged questions
              const stepHasFlags = step.sections?.some(section =>
                section.questions?.some(q => questionFlags[q.id])
              )

              return (
                <div key={step.id} className="flex items-center" style={{ flex: idx < STEPS.length - 1 ? 1 : 'none' }}>
                  <button
                    onClick={() => {
                      setCurrentStepIndex(idx)
                      setCurrentSectionIndex(0)
                      updateAssessment(assessmentId, {
                        currentStep: step.id,
                        currentSection: 0
                      })
                    }}
                    className="flex flex-col items-center gap-1.5 group"
                    title={step.name}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all text-sm font-semibold ${
                        isCurrent
                          ? stepHasFlags
                            ? 'bg-orange-500 text-white shadow-[0_0_0_3px_rgba(249,115,22,0.25)]'
                            : 'bg-primary-600 dark:bg-accent-300 text-white dark:text-primary-900 shadow-[0_0_0_3px_rgba(83,108,124,0.2)] dark:shadow-[0_0_0_3px_rgba(138,208,214,0.3)]'
                          : stepComplete
                          ? ''
                          : stepHasFlags
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 ring-2 ring-orange-300 dark:ring-orange-700'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400'
                      }`}
                      style={{
                        ...(stepComplete && !isCurrent && !stepHasFlags ? { backgroundColor: '#dcfce7', color: '#15803d' } : {}),
                        ...(stepComplete && !isCurrent && stepHasFlags ? { backgroundColor: '#fed7aa', color: '#c2410c' } : {})
                      }}
                    >
                      {stepComplete && !isCurrent ? (
                        stepHasFlags ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-1.81.452V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span
                      className="text-xs font-medium whitespace-nowrap transition-colors text-gray-700 dark:text-gray-200"
                    >
                      {step.name}
                    </span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`h-px flex-1 mx-2 mt-[-18px] ${
                        isPast || (stepComplete && idx < currentStepIndex) ? '' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                      style={{
                        ...(isPast || (stepComplete && idx < currentStepIndex) ? { backgroundColor: '#bbf7d0' } : {})
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content area with spacing from sticky header */}
      <div className="mt-6">
        {/* Top Navigation Buttons */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handlePrevious}
            disabled={currentStepIndex === 0 && currentSectionIndex === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {progress.total - progress.answered} of {progress.total} questions remaining
          </div>

          {isLastSection ? (
            isEditMode ? (
              hasEdits ? (
                <button onClick={() => setShowChangeReasonModal(true)} className="btn-primary">
                  Save Changes
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              ) : (
                <button disabled className="btn-primary opacity-40 cursor-not-allowed">
                  Assessment Complete
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              )
            ) : (
              <button onClick={handleComplete} className="btn-primary">
                Complete Assessment
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )
          ) : (
            <button onClick={handleNext} className="btn-primary">
              Next
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
        {/* Risk Education Panel - shown only on Risk Appetite step */}
        {currentStep.id === 'risk-appetite' && <RiskEducationPanel />}

      {/* Technology Scoping Step */}
      {currentStep.id === 'technology-scoping' && (
        <div className="mb-6">
          <TechnologyScopingStep
            responses={localResponses}
            onSave={(key, data) => {
              const newResponses = { ...localResponses, [key]: data }
              setLocalResponses(newResponses)
              // Save the entire technologyScoping object
              Object.entries(data).forEach(([questionId, responseData]) => {
                saveResponse(assessmentId, 'technologyScoping', questionId, responseData)
              })
            }}
          />
        </div>
      )}

      {/* Engagement Context Section - Specialized UI for engagement driver questions */}
      {currentStep.id === 'business-scoping' && currentSection.id === 'engagement-context' && (
        <div className="mb-6">
          <EngagementDriverSection
            responses={localResponses.businessScoping || {}}
            onResponseChange={(questionId, value, weight) => {
              handleResponseChange(questionId, value, weight)
            }}
          />
        </div>
      )}

      {/* Section Header - Only for steps with traditional questions (skip engagement-context which has its own UI) */}
      {currentStep.id !== 'technology-scoping' && currentSection.id !== 'engagement-context' && <div className="card mb-6">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{currentSection.name}</h2>
              {currentSection.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{currentSection.description}</p>
              )}
              {currentStep.sections.length > 1 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Section {currentSectionIndex + 1} of {currentStep.sections.length}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {(currentSection?.questions?.filter(q => questionFlags[q.id]).length || 0) > 0 && (
                <button
                  onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    showFlaggedOnly
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 ring-1 ring-orange-300 dark:ring-orange-700'
                      : 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                  }`}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-1.81.452V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z" clipRule="evenodd" />
                  </svg>
                  {currentSection?.questions?.filter(q => questionFlags[q.id]).length} flagged
                </button>
              )}
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {sectionProgress.total - sectionProgress.answered} of {sectionProgress.total} remaining
              </span>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {currentSection.questions.filter(question => {
            // Check conditional visibility (showWhen)
            if (question.showWhen) {
              const depResponse = localResponses.businessScoping?.[question.showWhen.questionId]
              const depValue = typeof depResponse === 'object' ? depResponse?.value : depResponse
              if (!depValue || !question.showWhen.values.includes(depValue)) {
                return false
              }
            }
            // Filter to flagged only when active
            if (showFlaggedOnly && !questionFlags[question.id]) return false
            return true
          }).map((question, qIdx) => {
            const disableInfo = currentStep.id === 'maturity-assessment' ? autoDisabledQuestions[question.id] : null
            const isAutoDisabled = !!disableInfo

            // Auto-disabled question: render collapsed with override option
            if (isAutoDisabled) {
              return (
                <div key={question.id} className="px-6 py-3 bg-gray-50 dark:bg-gray-800/30">
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500">
                      {qIdx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-400 dark:text-gray-500">{question.question}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                        Auto-excluded: {disableInfo.reason}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">N/A</span>
                      <button
                        onClick={() => {
                          setAutoDisableOverrides(prev => ({ ...prev, [question.id]: true }))
                          saveResponse(assessmentId, 'autoDisableOverrides', question.id, true)
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium whitespace-nowrap"
                      >
                        Include anyway
                      </button>
                    </div>
                  </div>
                </div>
              )
            }

            return (
            <div key={question.id} className={`p-6 ${questionFlags[question.id] ? 'bg-orange-50/50 dark:bg-orange-900/20' : ''}`}>
              <div className="flex items-start space-x-4">
                <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  questionFlags[question.id]
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 ring-2 ring-orange-300 dark:ring-orange-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>
                  {qIdx + 1}
                </span>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <p className="text-gray-900 dark:text-white font-medium">{question.question}</p>
                    <button
                      onClick={() => toggleFlag(question.id)}
                      title={questionFlags[question.id] ? 'Remove flag' : 'Flag for follow-up'}
                      className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        questionFlags[question.id]
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                          : 'text-gray-400 dark:text-gray-500 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10'
                      }`}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-1.81.452V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z" clipRule="evenodd" />
                      </svg>
                      {questionFlags[question.id] && <span>Flag</span>}
                    </button>
                  </div>

                  {/* Response Input */}
                  {renderQuestionInput(question)}

                  {/* Framework Mapping (for maturity assessment) */}
                  {question.frameworkMapping && (
                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">Frameworks:</span> {question.frameworkMapping}
                    </p>
                  )}

                  {/* Context factors tooltip (for maturity assessment) */}
                  {question.contextQuestions && currentStep.id === 'maturity-assessment' && (
                    <div className="mt-2 flex items-center text-xs text-gray-400 dark:text-gray-500">
                      <span className="font-medium">Context Factors</span>
                      <ContextTooltip contextQuestions={question.contextQuestions} />
                    </div>
                  )}

                  {/* Notes and Evidence actions */}
                  <div className="mt-4 flex flex-wrap gap-4">
                    {/* Notes section */}
                    <button
                      onClick={() => toggleNotes(question.id)}
                      className={`flex items-center space-x-2 text-sm transition-colors ${
                        questionNotes[question.id]
                          ? 'text-primary-600 dark:text-accent-300'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>
                        {questionNotes[question.id] ? 'Edit Note' : 'Add Note'}
                        {questionNotes[question.id] && (
                          <span className="ml-1 inline-flex items-center justify-center w-2 h-2 bg-green-500 rounded-full"></span>
                        )}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${expandedNotes[question.id] ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Evidence Types reference (only for maturity assessment) */}
                    {question.evidenceTypes && question.evidenceTypes.length > 0 && (
                      <button
                        onClick={() => toggleEvidence(question.id)}
                        className={`flex items-center space-x-2 text-sm transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Evidence Types</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${expandedEvidence[question.id] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}

                  </div>

                  {/* Flag reason input (when flagged) */}
                  {questionFlags[question.id] && (
                    <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-800/50 rounded-lg">
                      <label className="text-xs font-medium text-orange-700 dark:text-orange-300 mb-1 block">Follow-up question or note:</label>
                      <input
                        type="text"
                        value={questionFlags[question.id]?.reason || ''}
                        onChange={(e) => updateFlagReason(question.id, e.target.value)}
                        placeholder="What do you need to ask the client?"
                        className="w-full px-3 py-1.5 text-sm border border-orange-200 dark:border-orange-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      />
                    </div>
                  )}

                  {/* Expanded Notes panel */}
                  {expandedNotes[question.id] && (
                    <div className="mt-3">
                      <textarea
                        value={questionNotes[question.id] || ''}
                        onChange={(e) => handleNoteChange(question.id, e.target.value)}
                        placeholder="Add notes, observations, or context for this question..."
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                        rows={3}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Notes are saved automatically and included in the assessment report.
                      </p>
                    </div>
                  )}

                  {/* Expanded Evidence panel — reference list of expected evidence */}
                  {expandedEvidence[question.id] && question.evidenceTypes && (
                    <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Expected Evidence
                      </p>
                      <ul className="space-y-1.5">
                        {question.evidenceTypes.map((evidenceItem, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 mt-2" />
                            <span>{evidenceItem}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )
          })}
        </div>
      </div>}

        {/* Bottom Navigation Buttons */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={handlePrevious}
            disabled={currentStepIndex === 0 && currentSectionIndex === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {progress.total - progress.answered} of {progress.total} questions remaining
          </div>

          {isLastSection ? (
            isEditMode ? (
              hasEdits ? (
                <button onClick={() => setShowChangeReasonModal(true)} className="btn-primary">
                  Save Changes
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              ) : (
                <button disabled className="btn-primary opacity-40 cursor-not-allowed">
                  Assessment Complete
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              )
            ) : (
              <button onClick={handleComplete} className="btn-primary">
                Complete Assessment
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )
          ) : (
            <button onClick={handleNext} className="btn-primary">
              Next
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/* Change Reason Modal (edit mode) */}
      {showChangeReasonModal && (
        <ChangeReasonModal
          changeCount={computeAssessmentDiff(originalResponsesRef.current || {}, localResponses).length}
          sectionCount={new Set(computeAssessmentDiff(originalResponsesRef.current || {}, localResponses).map(c => c.section)).size}
          onConfirm={handleEditSave}
          onCancel={() => setShowChangeReasonModal(false)}
          saving={editSaving}
        />
      )}
    </div>
  )
}

export default Assessment
